'use client'
import Image from 'next/image'
import dynamic from 'next/dynamic'

import { cn } from '@/utils/twMerge'
import { PrismicLink } from '@prismicio/react'
import { PrismicNextLink } from '@prismicio/next'
import { usePathname } from 'next/navigation'
import React, { useEffect, useState, useCallback, useMemo, useRef, useId } from 'react'

import { getNavBgFromPathname } from '@/utils/darkNavRoute'
import { useLocale } from '@/context/LocaleContext'
import { getLangPath } from '@/utils/getLangFormat'
import { useTranslation } from '@/hooks/useTranslation'

import type { MegaKey } from '@/components/navbar/megaKeys'
import NavbarMegaTray, { type NavbarMegaTrayHandle } from '@/components/navbar/NavbarMegaTray'
import type { NavbarProps } from '@/types/layout'

const Hellobar = dynamic(() => import('../common/Hellobar'))
const MobileMenuDropdown = dynamic(() => import('@/components/navbar/mobile/MobileMenuDropdown'))
const NavbarDropdownItem = dynamic(() => import('@/components/navbar/NavbarDropdownItem'))

const LEAVE_DELAY_MS = 140

const Navbar = React.memo(function Navbar({
  navbar,
  hellobar,
  navBg,
  initialShowHellobar = false,
}: NavbarProps) {
  const pathname = usePathname()
  const resolvedNavBg = navBg ?? getNavBgFromPathname(pathname)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const mobileMenuToggleRef = useRef<HTMLButtonElement>(null)
  const megaTrayRef = useRef<NavbarMegaTrayHandle>(null)
  const [showHellobar, setShowHellobar] = useState(initialShowHellobar)
  const [activeMegaKey, setActiveMegaKey] = useState<MegaKey | null>(null)
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const megaTrayId = useId()
  const locale = useLocale()
  const { t } = useTranslation(locale)

  // Soft navigations can keep Navbar mounted while Layout re-resolves the cookie.
  // Sync server prop; local dismiss still wins until the next navigation updates the prop.
  useEffect(() => {
    setShowHellobar(initialShowHellobar)
  }, [initialShowHellobar])

  useEffect(() => {
    return () => {
      if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current)
    }
  }, [])

  const clearLeaveTimer = useCallback(() => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current)
      leaveTimerRef.current = null
    }
  }, [])

  const openMega = useCallback(
    (key: MegaKey) => {
      clearLeaveTimer()
      setActiveMegaKey(key)
    },
    [clearLeaveTimer],
  )

  const scheduleCloseMega = useCallback(() => {
    clearLeaveTimer()
    leaveTimerRef.current = setTimeout(() => {
      setActiveMegaKey(null)
      leaveTimerRef.current = null
    }, LEAVE_DELAY_MS)
  }, [clearLeaveTimer])

  const closeMegaNow = useCallback(() => {
    clearLeaveTimer()
    setActiveMegaKey(null)
  }, [clearLeaveTimer])

  const cancelCloseMega = useCallback(() => {
    clearLeaveTimer()
  }, [clearLeaveTimer])

  const enterMegaMenu = useCallback(() => {
    clearLeaveTimer()
    megaTrayRef.current?.focusFirst()
  }, [clearLeaveTimer])

  const navbarLogo = useMemo(() => {
    const logoSrc = {
      dark: '/images/sf-logo-white.svg',
      light: '/images/sf-logo.svg',
    }
    return logoSrc[resolvedNavBg as keyof typeof logoSrc]
  }, [resolvedNavBg])

  const rootPath = useMemo(() => `/${getLangPath(locale)}`, [locale])
  const pricingPath = useMemo(() => `/${getLangPath(locale)}/pricing`, [locale])
  const customersPath = useMemo(() => `/${getLangPath(locale)}/customers`, [locale])
  const requestDemoPath = useMemo(() => `/${getLangPath(locale)}/request-demo`, [locale])

  const handleMobileMenuToggle = useCallback(() => {
    setMobileMenuOpen((prev) => !prev)
  }, [])

  const handleMobileMenuClose = useCallback(() => {
    setMobileMenuOpen(false)
    mobileMenuToggleRef.current?.focus()
  }, [])

  useEffect(() => {
    if (!activeMegaKey) return

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      const nav = document.querySelector('.navbar.navbar--apple-spike')
      if (nav && !nav.contains(target)) {
        closeMegaNow()
      }
    }

    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [activeMegaKey, closeMegaNow])

  return (
    <React.Fragment>
      {showHellobar && hellobar && <Hellobar setShowHellobar={setShowHellobar} data={hellobar.data} />}

      <nav className={cn('navbar navbar--apple-spike', resolvedNavBg)} aria-label="Main navigation">
        <div className="container-fluid">
          <div className="navbar-wrapper">
            <div className="navbar-brand">
              <PrismicLink href={rootPath}>
                <Image src={navbarLogo} width={172} height={24} alt="sf-logo" />
              </PrismicLink>
            </div>

            <div className="navbar-content">
              <ul className="navbar-nav">
                <NavbarDropdownItem
                  className="products-dropdown"
                  label={t('products')}
                  megaKey="products"
                  isOpen={activeMegaKey === 'products'}
                  menuId={megaTrayId}
                  onOpen={openMega}
                  onRequestClose={scheduleCloseMega}
                  onDismiss={closeMegaNow}
                  onEnterMenu={enterMegaMenu}
                />

                <NavbarDropdownItem
                  className="solutions-dropdown"
                  label={t('solutions')}
                  megaKey="solutions"
                  isOpen={activeMegaKey === 'solutions'}
                  menuId={megaTrayId}
                  onOpen={openMega}
                  onRequestClose={scheduleCloseMega}
                  onDismiss={closeMegaNow}
                  onEnterMenu={enterMegaMenu}
                />

                <li className="">
                  <PrismicLink href={locale === 'en-us' ? '/customers' : customersPath} className="navbar-nav-link">
                    <span className="text-base font-normal">{t('customers')}</span>
                  </PrismicLink>
                </li>

                <NavbarDropdownItem
                  className="partners-dropdown"
                  label={t('partners')}
                  megaKey="partners"
                  isOpen={activeMegaKey === 'partners'}
                  menuId={megaTrayId}
                  onOpen={openMega}
                  onRequestClose={scheduleCloseMega}
                  onDismiss={closeMegaNow}
                  onEnterMenu={enterMegaMenu}
                />

                <li className="">
                  <PrismicLink href={locale === 'en-us' ? '/pricing' : pricingPath} className="navbar-nav-link">
                    <span className="text-base font-normal">{t('pricing')}</span>
                  </PrismicLink>
                </li>

                <NavbarDropdownItem
                  className="resources-dropdown"
                  label={t('resources')}
                  megaKey="resources"
                  isOpen={activeMegaKey === 'resources'}
                  menuId={megaTrayId}
                  onOpen={openMega}
                  onRequestClose={scheduleCloseMega}
                  onDismiss={closeMegaNow}
                  onEnterMenu={enterMegaMenu}
                />

                <NavbarDropdownItem
                  className="company-dropdown"
                  label={t('company')}
                  megaKey="company"
                  isOpen={activeMegaKey === 'company'}
                  menuId={megaTrayId}
                  onOpen={openMega}
                  onRequestClose={scheduleCloseMega}
                  onDismiss={closeMegaNow}
                  onEnterMenu={enterMegaMenu}
                />
              </ul>
            </div>

            <div className="navbar-action hidden lg:inline-flex">
              <PrismicNextLink field={navbar.data.login_link} className="btn btn-text pr-5">
                <span
                  className={cn(
                    'text-base font-normal',
                    resolvedNavBg === 'dark' ? 'text-white' : 'text-primary-ink-100',
                  )}>
                  {t('sign_in')}
                </span>
              </PrismicNextLink>

              <PrismicLink
                href={locale === 'en-us' ? '/request-demo' : requestDemoPath}
                className={cn(
                  'btn btn-small btn-interaction',
                  resolvedNavBg === 'dark' ? 'btn-white' : 'btn-secondary !text-primary-ink-100',
                )}>
                {t('request_a_demo')}
                <Image
                  src={`/images/angle-right-${resolvedNavBg === 'dark' ? 'green' : 'white'}.svg`}
                  width={15}
                  height={9}
                  alt=""
                  aria-hidden="true"
                  className={cn('!mt-0 shrink-0 self-center', resolvedNavBg !== 'dark' && 'brightness-0')}
                />
              </PrismicLink>
            </div>

            <button
              ref={mobileMenuToggleRef}
              type="button"
              className="navbar-mobile-toggle"
              onClick={handleMobileMenuToggle}
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu">
              <span className="cursor-pointer text-sm font-bold text-primary-ink-100">
                {mobileMenuOpen ? 'Close' : 'Menu'}
              </span>
            </button>
          </div>
        </div>

        <NavbarMegaTray
          ref={megaTrayRef}
          activeKey={activeMegaKey}
          data={navbar.data as unknown as Record<string, unknown>}
          trayId={megaTrayId}
          onClose={closeMegaNow}
          onPointerEnter={cancelCloseMega}
          onPointerLeave={scheduleCloseMega}
        />

        {mobileMenuOpen && <MobileMenuDropdown data={navbar} onClose={handleMobileMenuClose} />}
      </nav>
    </React.Fragment>
  )
})

export default Navbar
